"use client";

import { Fragment, useEffect, useMemo, useState } from "react";
import { getPublicPricing, PublicPricingResponse } from "@/lib/api";
import { BarChart3, Check, ChevronDown, ChevronRight, CircleDollarSign, Minus, ShieldCheck, Sparkles, Users } from "lucide-react";
import { normalizePublicCtaUrl, SIGNUP_DASHBOARD_URL } from "@/lib/auth-urls";

const PLANS = ["Seed", "Sprout", "Treehouse", "Rainforest"] as const;

type Plan = (typeof PLANS)[number];

const FEATURES = [
  "Custom domains",
  "Unlimited projects",
  "Team seats",
  "SLA & priority support",
  "Analytics & reporting",
  "API access",
  "Single sign-on (SSO)",
];

type FeatureValue = string | boolean | null;
type FeatureRow = { name: string; values: FeatureValue[]; category?: string | null };
type FeatureGroup = { name: string; rows: FeatureRow[] };

export function PricingComparison() {
  const [data, setData] = useState<PublicPricingResponse | null>(null);
  const [activePlanIndex, setActivePlanIndex] = useState(0);
  const [currency, setCurrency] = useState<string | null>("INR");
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [showDifferencesOnly, setShowDifferencesOnly] = useState(false);
  const [mobileCompareMode, setMobileCompareMode] = useState(false);
  const [mobileCompareA, setMobileCompareA] = useState(0);
  const [mobileCompareB, setMobileCompareB] = useState(1);
  const [monthlyClicks, setMonthlyClicks] = useState(5000);
  const [baseConversionRate, setBaseConversionRate] = useState(2.0);
  const [conversionLift, setConversionLift] = useState(15);
  const [avgOrderValue, setAvgOrderValue] = useState(1200);
  const [featureAnimationSeed, setFeatureAnimationSeed] = useState(0);
  const [calculatorAnimationSeed, setCalculatorAnimationSeed] = useState(0);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const billingMode = billingAnnual ? "yearly" : "monthly";

  function asFiniteNumber(value: unknown): number | null {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  function resolvePlanPrice(plan: any, mode: "monthly" | "yearly") {
    if (!plan) return null;

    const monthly = asFiniteNumber(plan.monthly) ?? asFiniteNumber(plan.priceMonthly) ?? asFiniteNumber(plan.monthlyPrice);
    const yearly = asFiniteNumber(plan.annual) ?? asFiniteNumber(plan.yearly) ?? asFiniteNumber(plan.priceYearly) ?? asFiniteNumber(plan.annualPrice);
    const computed = asFiniteNumber(plan.price);

    if (mode === "yearly") {
      if (yearly != null) return yearly;
      if (plan.billing === "yearly" && computed != null) return computed;
      return null;
    }

    if (monthly != null) return monthly;
    if (plan.billing === "monthly" && computed != null) return computed;
    return null;
  }

  useEffect(() => {
    let mounted = true;
    getPublicPricing({ currency: currency ?? undefined, billing: billingMode })
      .then((res) => {
        if (!mounted) return;
        setData(res);
      })
      .catch(() => {
        // silently fallback to hardcoded if API fails
      });
    return () => {
      mounted = false;
    };
  }, [currency, billingMode]);

  function displayPrice(plan: any) {
    if (!plan) return "—";
    const base = resolvePlanPrice(plan, billingMode);
    if (base == null) return "—";
    return `${plan.currency ?? currency ?? "₹"} ${Number(base).toLocaleString()}`;
  }

  function numericPrice(plan: any) {
    if (!plan) return 0;
    const base = resolvePlanPrice(plan, billingMode);
    const value = Number(base ?? 0);
    return Number.isFinite(value) ? value : 0;
  }

  const plans =
    data?.plans ??
    PLANS.map((p) => ({ id: p, name: p, monthly: null, annual: null, currency: null, image: null, highlight: p === "Treehouse" }));
  const featureRows: FeatureRow[] = (data?.features as FeatureRow[] | undefined) ?? FEATURES.map((name) => ({ name, values: PLANS.map(() => false) }));
  const features = featureRows.map((f) => f.name);

  const activePlan = plans[activePlanIndex] ?? plans[0];
  const maxPrice = Math.max(...plans.map((plan: any) => numericPrice(plan)), 1);

  const selectedMobileIndexes = mobileCompareMode ? [mobileCompareA, mobileCompareB] : [activePlanIndex];

  function inferCategory(name: string) {
    const value = name.toLowerCase();
    if (/(sso|security|governance|compliance|audit|privacy|access|permission)/.test(value)) return "Security";
    if (/(team|admin|role|seat|workspace|policy|approval|provisioning)/.test(value)) return "Admin";
    if (/(sla|support|priority|help|onboarding|success)/.test(value)) return "Support";
    return "Productivity";
  }

  function rowDiffersAcrossIndexes(row: FeatureRow, indexes: number[]) {
    const normalized = indexes.map((idx) => JSON.stringify(row.values?.[idx]));
    return new Set(normalized).size > 1;
  }

  const groupedFeatureRows = useMemo<FeatureGroup[]>(() => {
    const groups = new Map<string, FeatureRow[]>();

    featureRows.forEach((row) => {
      const category = row.category?.trim() || inferCategory(row.name);
      const list = groups.get(category) ?? [];
      list.push(row);
      groups.set(category, list);
    });

    const order = ["Productivity", "Security", "Admin", "Support"];
    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        const ai = order.indexOf(a);
        const bi = order.indexOf(b);
        const aRank = ai === -1 ? order.length : ai;
        const bRank = bi === -1 ? order.length : bi;
        if (aRank !== bRank) return aRank - bRank;
        return a.localeCompare(b);
      })
      .map(([name, rows]) => ({ name, rows }));
  }, [featureRows]);

  const visibleGroupedFeatureRows = useMemo<FeatureGroup[]>(() => {
    return groupedFeatureRows
      .map((group) => ({
        ...group,
        rows: showDifferencesOnly ? group.rows.filter((row) => rowDiffersAcrossIndexes(row, plans.map((_, idx) => idx))) : group.rows,
      }))
      .filter((group) => group.rows.length > 0);
  }, [groupedFeatureRows, showDifferencesOnly, plans]);

  const visibleMobileGroupedFeatureRows = useMemo<FeatureGroup[]>(() => {
    return groupedFeatureRows
      .map((group) => ({
        ...group,
        rows: showDifferencesOnly ? group.rows.filter((row) => rowDiffersAcrossIndexes(row, selectedMobileIndexes)) : group.rows,
      }))
      .filter((group) => group.rows.length > 0);
  }, [groupedFeatureRows, selectedMobileIndexes, showDifferencesOnly]);

  const includedFeatureCount = useMemo(() => {
    return featureRows.reduce((count, row) => {
      const value = row.values?.[activePlanIndex];
      if (value === true || value === "true" || value === "yes") return count + 1;
      return count;
    }, 0);
  }, [featureRows, activePlanIndex]);

  const comparablePlans = plans.filter((plan: any) => numericPrice(plan) > 0);
  const annualSavings = useMemo(() => {
    if (!billingAnnual || !activePlan) return null;
    const monthly = Number(resolvePlanPrice(activePlan, "monthly") ?? 0);
    const yearly = Number(resolvePlanPrice(activePlan, "yearly") ?? 0);
    if (!monthly || !yearly) return null;

    const monthlyAnnualized = monthly * 12;
    if (monthlyAnnualized <= yearly) return null;

    const amount = monthlyAnnualized - yearly;
    const percent = Math.round((amount / monthlyAnnualized) * 100);
    return { amount, percent };
  }, [billingAnnual, activePlan]);

  const projectedExtraRevenue = useMemo(() => {
    const baselineConversions = monthlyClicks * (baseConversionRate / 100);
    const additionalConversions = baselineConversions * (conversionLift / 100);
    return additionalConversions * avgOrderValue;
  }, [avgOrderValue, baseConversionRate, conversionLift, monthlyClicks]);

  const projectedRoiPercent = useMemo(() => {
    const planCost = numericPrice(activePlan);
    if (!planCost || projectedExtraRevenue <= 0) return null;
    return Math.round(((projectedExtraRevenue - planCost) / planCost) * 100);
  }, [activePlan, projectedExtraRevenue]);

  useEffect(() => {
    setFeatureAnimationSeed((seed) => seed + 1);
  }, [activePlanIndex, showDifferencesOnly, mobileCompareMode, mobileCompareA, mobileCompareB]);

  useEffect(() => {
    setCollapsedGroups((current) => {
      let changed = false;
      const next = { ...current };
      for (const group of groupedFeatureRows) {
        if (typeof next[group.name] === "undefined") {
          next[group.name] = false;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [groupedFeatureRows]);

  useEffect(() => {
    setCalculatorAnimationSeed((seed) => seed + 1);
  }, [projectedExtraRevenue, projectedRoiPercent, activePlanIndex, billingAnnual, currency]);

  useEffect(() => {
    if (plans.length < 2) return;
    if (mobileCompareA === mobileCompareB) {
      setMobileCompareB((mobileCompareA + 1) % plans.length);
    }
  }, [mobileCompareA, mobileCompareB, plans.length]);

  function renderFeatureValue(value: string | boolean | null | undefined) {
    if (typeof value === "boolean") {
      return value ? (
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
      ) : (
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
          <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
      );
    }

    if (value == null || value === "") {
      return <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"><Minus className="h-3.5 w-3.5" strokeWidth={2.5} /></span>;
    }

    return <span className="text-[13px] font-medium text-foreground">{String(value)}</span>;
  }

  function toggleGroup(name: string) {
    setCollapsedGroups((current) => ({
      ...current,
      [name]: !current[name],
    }));
  }

  const stickyPlans = plans.map((p: any) => ({
    name: p.name,
    price: displayPrice(p),
    priceSuffix: billingAnnual ? " / year" : " / month",
    ctaText: (p.ctaText as string) || "Buy Now",
    ctaUrl: normalizePublicCtaUrl((p.ctaUrl as string) || SIGNUP_DASHBOARD_URL),
    highlight: Boolean(p.highlight),
  }));

  return (
    <section id="pricing-comparison-section" className="relative overflow-x-clip bg-background/60 py-8 pb-32 lg:py-12 lg:pb-12">
      <div className="pointer-events-none absolute -left-24 top-0 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-28 bottom-0 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="pricing-comparison-header flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              Plan Comparison
            </p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-[2rem]">See exactly what comes with each plan.</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground sm:text-[15px]">
              Compare capabilities, estimate spend quickly, and choose the plan that aligns with your current growth stage.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1">
              <button onClick={() => setBillingAnnual(false)} className={`rounded-full px-3 py-1 text-xs font-semibold ${!billingAnnual ? "bg-primary text-white" : "text-muted-foreground"}`}>Monthly</button>
              <button onClick={() => setBillingAnnual(true)} className={`rounded-full px-3 py-1 text-xs font-semibold ${billingAnnual ? "bg-primary text-white" : "text-muted-foreground"}`}>Yearly</button>
            </div>
            <select value={currency ?? ""} onChange={(e) => setCurrency(e.target.value)} className="rounded-md border border-border bg-card px-2 py-1 text-xs font-medium">
              {(data?.currencies ?? ["INR"]).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <label className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
              <input
                type="checkbox"
                checked={showDifferencesOnly}
                onChange={(e) => setShowDifferencesOnly(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Show Differences Only
            </label>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Estimated Plan Cost</p>
              <CircleDollarSign className="h-4 w-4 text-primary" />
            </div>
            <p className="mt-2 text-2xl font-semibold text-foreground">{displayPrice(activePlan)}</p>
            <p className="mt-1 text-xs text-muted-foreground">{billingAnnual ? "Annual billing" : "Monthly billing"}</p>
            {annualSavings ? (
              <p className="mt-2 text-xs font-semibold text-emerald-600">
                Save {activePlan?.currency ?? currency ?? "₹"} {annualSavings.amount.toLocaleString()} ({annualSavings.percent}%) yearly
              </p>
            ) : null}
          </div>

          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Feature Coverage</p>
              <BarChart3 className="h-4 w-4 text-primary" />
            </div>
            <p className="mt-2 text-2xl font-semibold text-foreground">{includedFeatureCount} / {features.length}</p>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-violet-600 transition-all duration-500" style={{ width: `${Math.max(8, (includedFeatureCount / Math.max(features.length, 1)) * 100)}%` }} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Included boolean capabilities in selected plan</p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Support & Governance</p>
              <ShieldCheck className="h-4 w-4 text-primary" />
            </div>
            <p className="mt-2 text-lg font-semibold text-foreground">Built for scale-ready teams</p>
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              Team access, reporting, and rollout confidence
            </div>
          </div>
        </div>

        {comparablePlans.length > 0 ? (
          <div className="mt-5 rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Plan Value Snapshot</p>
              <p className="text-xs text-muted-foreground">Relative cost at current billing</p>
            </div>
            <div className="space-y-3">
              {comparablePlans.map((plan: any, idx: number) => {
                const value = numericPrice(plan);
                const width = Math.max(10, Math.round((value / maxPrice) * 100));
                return (
                  <div key={plan.id} className="grid grid-cols-[120px_1fr_72px] items-center gap-3">
                    <p className={`truncate text-xs font-semibold ${plan.highlight ? "text-primary" : "text-foreground"}`}>{plan.name}</p>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div className="shimmer-bar h-full rounded-full bg-gradient-to-r from-blue-600 via-violet-500 to-fuchsia-500" style={{ width: `${width}%`, animationDelay: `${idx * 90}ms` }} />
                    </div>
                    <p className="text-right text-xs font-medium text-muted-foreground">{displayPrice(plan)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mt-5 rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">ROI Projection Calculator</p>
            <p className="text-xs text-muted-foreground">Scenario-based estimate</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <label className="text-xs text-muted-foreground">
              Monthly Clicks
              <input type="number" min={0} value={monthlyClicks} onChange={(e) => setMonthlyClicks(Math.max(0, Number(e.target.value) || 0))} className="mt-1 w-full rounded-md border bg-background px-2 py-1.5 text-sm text-foreground" />
            </label>
            <label className="text-xs text-muted-foreground">
              Base Conversion %
              <input type="number" min={0} step="0.1" value={baseConversionRate} onChange={(e) => setBaseConversionRate(Math.max(0, Number(e.target.value) || 0))} className="mt-1 w-full rounded-md border bg-background px-2 py-1.5 text-sm text-foreground" />
            </label>
            <label className="text-xs text-muted-foreground">
              Expected Lift %
              <input type="number" min={0} value={conversionLift} onChange={(e) => setConversionLift(Math.max(0, Number(e.target.value) || 0))} className="mt-1 w-full rounded-md border bg-background px-2 py-1.5 text-sm text-foreground" />
            </label>
            <label className="text-xs text-muted-foreground">
              Avg Order Value ({activePlan?.currency ?? currency ?? "₹"})
              <input type="number" min={0} value={avgOrderValue} onChange={(e) => setAvgOrderValue(Math.max(0, Number(e.target.value) || 0))} className="mt-1 w-full rounded-md border bg-background px-2 py-1.5 text-sm text-foreground" />
            </label>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border/70 bg-background p-3">
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Projected Extra Revenue</p>
              <p key={`rev-${calculatorAnimationSeed}`} className="premium-value-enter mt-1 text-base font-bold text-foreground">
                {activePlan?.currency ?? currency ?? "₹"} {Math.round(projectedExtraRevenue).toLocaleString()}
              </p>
            </div>
            <div className="rounded-xl border border-border/70 bg-background p-3">
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Selected Plan Cost</p>
              <p key={`cost-${calculatorAnimationSeed}`} className="premium-value-enter mt-1 text-base font-bold text-foreground">{displayPrice(activePlan)}</p>
            </div>
            <div className="rounded-xl border border-border/70 bg-background p-3">
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Estimated ROI</p>
              <p
                key={`roi-${calculatorAnimationSeed}`}
                className={`premium-value-enter mt-1 text-base font-bold ${projectedRoiPercent != null && projectedRoiPercent >= 0 ? "text-emerald-600" : "text-foreground"}`}
              >
                {projectedRoiPercent == null ? "—" : `${projectedRoiPercent}%`}
              </p>
            </div>
          </div>
        </div>

        <div className="sticky top-[4.5rem] z-50 mt-8 mb-4 rounded-xl bg-background/90 p-2 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:top-[5.25rem]">
          <div className="mb-3 hidden items-center justify-center gap-1 rounded-full border border-border bg-card p-1 lg:hidden">
            <button
              onClick={() => setMobileCompareMode(false)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${!mobileCompareMode ? "bg-primary text-white" : "text-muted-foreground"}`}
            >
              Single Plan
            </button>
            <button
              onClick={() => setMobileCompareMode(true)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${mobileCompareMode ? "bg-primary text-white" : "text-muted-foreground"}`}
            >
              Compare 2 Plans
            </button>
          </div>

          <div className="mb-4 flex flex-wrap justify-center gap-1 rounded-full border border-border bg-card p-1">
            {plans.map((p, idx) => (
              <button
                key={p.id}
                onClick={() => setActivePlanIndex(idx)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${idx === activePlanIndex ? (p.highlight ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow' : 'bg-primary text-white') : 'text-muted-foreground'}`}
              >
                {p.name}
              </button>
            ))}
          </div>

          {mobileCompareMode ? (
            <div className="mb-1 grid grid-cols-2 gap-2 lg:hidden">
              <select value={mobileCompareA} onChange={(e) => setMobileCompareA(Number(e.target.value))} className="rounded-md border bg-card px-2 py-1.5 text-xs">
                {plans.map((plan, idx) => (
                  <option key={`${plan.id}-a`} value={idx}>{plan.name}</option>
                ))}
              </select>
              <select value={mobileCompareB} onChange={(e) => setMobileCompareB(Number(e.target.value))} className="rounded-md border bg-card px-2 py-1.5 text-xs">
                {plans.map((plan, idx) => (
                  <option key={`${plan.id}-b`} value={idx}>{plan.name}</option>
                ))}
              </select>
            </div>
          ) : null}
        </div>

        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <div className="order-2 lg:order-1">
            <div className="mx-0 overflow-hidden rounded-xl border bg-card p-3 lg:sticky lg:top-24">
              <div className="px-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-foreground">Features</h3>
                  <span className="hidden text-xs text-muted-foreground sm:inline">{showDifferencesOnly ? "Differences only" : "Toggle between plans on mobile"}</span>
                </div>
                <div className="mt-3 space-y-2">
                  {visibleGroupedFeatureRows.map((group) => {
                    const isCollapsed = Boolean(collapsedGroups[group.name]);
                    return (
                      <div key={group.name} className="overflow-hidden rounded-lg border border-border/80">
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.name)}
                          className="flex w-full items-center justify-between bg-muted/30 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                        >
                          <span>{group.name}</span>
                          {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                        {!isCollapsed ? (
                          <ul className="divide-y divide-border/70">
                            {group.rows.map((row, idx) => (
                              <li
                                key={`${featureAnimationSeed}-${group.name}-${row.name}`}
                                className="premium-row-enter py-2.5 pl-3 pr-2 text-[13px] text-foreground"
                                style={{ animationDelay: `${Math.min(idx * 18, 180)}ms` }}
                              >
                                {row.name}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="hidden lg:block">
              <div className="overflow-x-auto rounded-xl border bg-card p-0">
                <table className="w-full table-fixed border-collapse" style={{ minWidth: `${280 + plans.length * 180}px` }}>
                  <thead>
                    <tr className="border-b border-border bg-muted/20">
                      <th className="w-[280px] left-0 sticky bg-muted/20 py-4 pl-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground z-20">Features</th>
                      {plans.map((p) => (
                        <th key={p.id} style={{ width: `${180}px` }} className="py-4 text-center text-sm font-semibold text-foreground">
                          <div className="flex flex-col items-center gap-1 px-2">
                            {p.image ? <img src={p.image} alt={p.name} className="h-10 w-10 object-contain" /> : null}
                            <div className={`text-sm font-semibold ${p.highlight ? "bg-gradient-to-br from-blue-600 to-violet-600 bg-clip-text text-transparent" : "text-foreground"}`}>{p.name}</div>
                            <div className="text-xs font-medium text-muted-foreground">{displayPrice(p)}</div>
                            <a
                              href={normalizePublicCtaUrl((p as any)?.ctaUrl || SIGNUP_DASHBOARD_URL)}
                              className={`mt-1 inline-flex items-center justify-center rounded-md px-3 py-1.5 text-[11px] font-semibold ${p.highlight ? "bg-primary text-white" : "border border-border text-foreground hover:bg-muted"}`}
                            >
                              {(p as any)?.ctaText || "Choose"}
                            </a>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {visibleGroupedFeatureRows.map((group) => {
                      const isCollapsed = Boolean(collapsedGroups[group.name]);
                      return (
                        <Fragment key={group.name}>
                          <tr key={`group-${group.name}`}>
                            <td colSpan={plans.length + 1} className="bg-muted/30 px-0 py-0">
                              <button
                                type="button"
                                onClick={() => toggleGroup(group.name)}
                                className="flex w-full items-center justify-between px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                              >
                                <span>{group.name}</span>
                                {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                              </button>
                            </td>
                          </tr>
                          {!isCollapsed
                            ? group.rows.map((row, idx) => (
                                <tr
                                  key={`${featureAnimationSeed}-${group.name}-${row.name}`}
                                  className="premium-row-enter group align-top"
                                  style={{ animationDelay: `${Math.min(idx * 16, 170)}ms` }}
                                >
                                  <td className="left-0 sticky bg-card py-2.5 pl-4 pr-2 text-[13px] text-foreground z-10 transition-colors group-hover:bg-blue-50/60 dark:group-hover:bg-blue-950/20">
                                    {row.name}
                                  </td>
                                  {row.values.map((val, j) => (
                                    <td key={j} style={{ width: `${180}px` }} className="py-2.5 text-center transition-colors group-hover:bg-blue-50/60 dark:group-hover:bg-blue-950/20">
                                      {renderFeatureValue(val)}
                                    </td>
                                  ))}
                                </tr>
                              ))
                            : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>

                <div className="sticky bottom-0 z-20 border-t border-border bg-card/95 backdrop-blur">
                  <div className="grid" style={{ gridTemplateColumns: `280px repeat(${plans.length}, 180px)`, minWidth: `${280 + plans.length * 180}px` }}>
                    <div className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Ready To Start?</div>
                    {plans.map((p) => (
                      <div key={`sticky-cta-${p.id}`} className="px-2 py-2">
                        <a
                          href={normalizePublicCtaUrl((p as any)?.ctaUrl || SIGNUP_DASHBOARD_URL)}
                          className={`inline-flex w-full items-center justify-center rounded-md px-3 py-2 text-xs font-semibold ${p.highlight ? "bg-primary text-white" : "border border-border text-foreground hover:bg-muted"}`}
                        >
                          {(p as any)?.ctaText || "Choose"}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="block lg:hidden">
              <div className="rounded-xl border bg-card p-4">
                {!mobileCompareMode ? (
                  <>
                    <div className="mb-4 flex items-center justify-between">
                      <h4 className="text-lg font-semibold">{plans[activePlanIndex]?.name}</h4>
                      <div className="text-sm text-muted-foreground">{displayPrice(plans[activePlanIndex])}</div>
                    </div>
                    <a
                      href={normalizePublicCtaUrl((plans[activePlanIndex] as any)?.ctaUrl || SIGNUP_DASHBOARD_URL)}
                      className={`mb-3 inline-flex w-full items-center justify-center rounded-md px-3 py-2 text-xs font-semibold ${(plans[activePlanIndex] as any)?.highlight ? "bg-primary text-white" : "border border-border text-foreground"}`}
                    >
                      {(plans[activePlanIndex] as any)?.ctaText || "Choose Plan"}
                    </a>
                    <div className="space-y-2">
                      {visibleMobileGroupedFeatureRows.map((group) => {
                        const isCollapsed = Boolean(collapsedGroups[group.name]);
                        return (
                          <div key={`mobile-single-${group.name}`} className="overflow-hidden rounded-lg border border-border/80">
                            <button
                              type="button"
                              onClick={() => toggleGroup(group.name)}
                              className="flex w-full items-center justify-between bg-muted/30 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                            >
                              <span>{group.name}</span>
                              {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                            {!isCollapsed ? (
                              <ul className="divide-y divide-border/70">
                                {group.rows.map((row, idx) => (
                                  <li
                                    key={`${featureAnimationSeed}-${group.name}-${row.name}`}
                                    className="premium-row-enter flex items-center justify-between py-2.5 pl-3 pr-2 text-[13px]"
                                    style={{ animationDelay: `${Math.min(idx * 16, 170)}ms` }}
                                  >
                                    <span>{row.name}</span>
                                    <span>{renderFeatureValue(row.values?.[activePlanIndex])}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3 grid grid-cols-[1fr_1fr_1fr] items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      <span>Feature</span>
                      <span className="text-center">{plans[mobileCompareA]?.name}</span>
                      <span className="text-center">{plans[mobileCompareB]?.name}</span>
                    </div>
                    <div className="space-y-2">
                      {visibleMobileGroupedFeatureRows.map((group) => {
                        const isCollapsed = Boolean(collapsedGroups[group.name]);
                        return (
                          <div key={`mobile-compare-${group.name}`} className="overflow-hidden rounded-lg border border-border/80">
                            <button
                              type="button"
                              onClick={() => toggleGroup(group.name)}
                              className="flex w-full items-center justify-between bg-muted/30 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                            >
                              <span>{group.name}</span>
                              {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                            {!isCollapsed ? (
                              <ul className="divide-y divide-border/70">
                                {group.rows.map((row, idx) => (
                                  <li
                                    key={`${featureAnimationSeed}-${group.name}-${row.name}`}
                                    className="premium-row-enter grid grid-cols-[1fr_1fr_1fr] items-center gap-2 py-2.5 pl-3 pr-2 text-xs"
                                    style={{ animationDelay: `${Math.min(idx * 16, 170)}ms` }}
                                  >
                                    <span className="text-foreground">{row.name}</span>
                                    <span className="flex justify-center">{renderFeatureValue(row.values?.[mobileCompareA])}</span>
                                    <span className="flex justify-center">{renderFeatureValue(row.values?.[mobileCompareB])}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className="fixed z-50 rounded-2xl border border-border bg-card/95 p-3 shadow-xl backdrop-blur lg:hidden"
          style={{
            left: "max(0.5rem, env(safe-area-inset-left))",
            right: "max(0.5rem, env(safe-area-inset-right))",
            bottom: "max(0.5rem, env(safe-area-inset-bottom))",
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Selected Plan</p>
              <p className="truncate text-sm font-bold text-foreground">{activePlan?.name || "Plan"} · {displayPrice(activePlan)}</p>
            </div>
            <a
              href={normalizePublicCtaUrl((activePlan as any)?.ctaUrl || SIGNUP_DASHBOARD_URL)}
              className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-2 text-xs font-bold text-white sm:w-auto"
            >
              {(activePlan as any)?.ctaText || "Choose Plan"}
            </a>
          </div>
        </div>

        <style jsx>{`
          .premium-row-enter {
            animation: premiumRowIn 280ms cubic-bezier(0.22, 1, 0.36, 1) both;
          }

          .premium-value-enter {
            animation: premiumValueIn 240ms cubic-bezier(0.22, 1, 0.36, 1) both;
          }

          @keyframes premiumRowIn {
            from {
              opacity: 0;
              transform: translateY(6px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes premiumValueIn {
            from {
              opacity: 0.55;
              transform: translateY(4px) scale(0.985);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .premium-row-enter,
            .premium-value-enter {
              animation: none;
            }
          }
        `}</style>
      </div>
    </section>
  );
}

export default PricingComparison;
